import md5 from 'md5';
import { IDeviceAdapter, PersonData, DeviceOperation, DeviceInfo } from './base';
import {
  SessionInfo,
  FirmwareInfo,
  SessionEstablishmentRequest,
  SessionEstablishmentResult,
} from '../session/types';
import { createHash } from 'crypto-browserify';
import { deviceBaseUrl } from '../../utils/deviceEndpoint';

/**
 * Hikvision Device Adapter
 * Formats payloads according to Hikvision ISAPI specification
 */
export class HikvisionAdapter implements IDeviceAdapter {
  vendor = 'hikvision' as const;

  /** Cached firmware auth requirement per device IP. Key = IP string, Value = {requiresSession, cachedAt} */
  private firmwareCache = new Map<string, { requiresSession: boolean; cachedAt: number }>();
  private readonly FIRMWARE_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  /**
   * Generate MD5 hash of person ID (matches backend behavior)
   * Backend uses: hash := md5.Sum([]byte(person.PersonID)); hex.EncodeToString(hash[:])
   * This creates a deterministic hash that matches across frontend and backend
   */
  private generateEmployeeNo(personId: string): string {
    // Use real MD5 hash to match backend implementation
    return md5(personId);
  }

  /**
   * Public method to generate device person ID from PersonData
   * This allows the service to get the employee number before sync
   */
  generateDevicePersonId(personData: PersonData): string {
    return this.generateEmployeeNo(personData.person_id);
  }

  /**
   * Format person sync payload for Hikvision
   * Endpoint: /ISAPI/AccessControl/UserInfo/Record?format=json
   *
   * CRITICAL: Hikvision requires payload wrapped in "UserInfo" object
   *
   * NOTE: Face photo upload is a SEPARATE API call to /ISAPI/AccessControl/UserInfo/SetFace
   * Backend handles photo sync separately. Frontend can implement this later if needed.
   */
  /**
   * Convert an ISO8601 string (UTC) to a local datetime string for Hikvision.
   * Hikvision devices interpret datetime strings as device-local time (no timezone),
   * so we must pass local time, not UTC.
   */
  private toLocalHikvisionDateTime(isoString: string): string {
    const d = new Date(isoString);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  formatSyncPersonPayload(personData: PersonData): any {
    const employeeNo = this.generateEmployeeNo(personData.person_id);
    const fullName = `${personData.first_name} ${personData.last_name}`.trim();

    const payload = {
      UserInfo: {  // ← CRITICAL: Must wrap in UserInfo
        employeeNo: employeeNo,
        name: fullName,
        userType: personData.isVisitor ? 'visitor' : 'normal',
        closeDelayEnabled: false,  // Backend sends this as zero value
        Valid: {
          enable: personData.isAllowed !== false, // Default true
          beginTime: personData.validFrom
            ? this.toLocalHikvisionDateTime(personData.validFrom)
            : '2020-01-01T00:00:00',
          endTime: personData.validUntil
            ? this.toLocalHikvisionDateTime(personData.validUntil)
            : '2030-12-31T23:59:59',
          timeType: '',  // Backend sends empty string (Go zero value)
        },
        doorRight: '1',
        RightPlan: [
          {
            doorNo: 1,
            planTemplateNo: '1',
          },
        ],
        // REMOVED: userVerifyMode - device rejects this field with error "badJsonContent"
        // Device error: {"statusCode": 6, "statusString": "Invalid Content", "errorMsg": "userVerifyMode"}
        // groupBelong: [],  // Also removed - try minimal payload first
      },
    };

    console.log(' [Hikvision] Payload details:', {
      personId: personData.person_id,
      employeeNo: employeeNo,
      fullName: fullName,
      payloadSize: JSON.stringify(payload).length,
      payload: JSON.stringify(payload, null, 2),
    });

    return payload;
  }

  /**
   * Format person removal payload
   * Endpoint: /ISAPI/AccessControl/UserInfo/Delete?format=json
   */
  formatRemovePersonPayload(devicePersonId: string): any {
    // Safety guard: never build a delete whose filter targets nothing.
    //
    // UserInfo/Delete with an empty employeeNo list is documented firmware behaviour for this
    // family — it deletes EVERY enrolled user (testing/fakedevices reproduces it on purpose
    // because it has cost real customers their enrollment table). An empty devicePersonId
    // produced exactly that request shape, silently, from any caller that reached here without a
    // device-side id.
    //
    // The ControlID adapter has carried this same guard for a while; this one did not, which was
    // an asymmetry rather than a decision.
    if (!devicePersonId || String(devicePersonId).trim() === '') {
      throw new Error('formatRemovePersonPayload: devicePersonId is required and cannot be empty');
    }

    return {
      UserInfoDelCond: {
        EmployeeNoList: [
          {
            employeeNo: devicePersonId,
          },
        ],
      },
    };
  }

  /**
   * Format face photo upload payload for Hikvision FDLib endpoint.
   * Endpoint: POST /ISAPI/Intelligent/FDLib/FaceDataRecord?format=json&faceLibType=blackFD&FDID=1&FPID={employeeNo}
   *
   * Face recognition terminals (DS-K1T341 etc.) reject the legacy
   * PUT /ISAPI/AccessControl/UserInfo/SetFace with statusCode=4 "Invalid Operation".
   * They require a multipart POST to FDLib instead (same as the Go backend).
   *
   * The {FPID} placeholder in getEndpoint() is replaced with the real employeeNo
   * inside uploadFacePhoto() in deviceCommunication.ts before making the request.
   */
  formatUploadFacePayload(employeeNo: string, photoBase64: string): any {
    // Strip data URI prefix if present
    const rawBase64 = photoBase64.startsWith('data:')
      ? photoBase64.split(',')[1]
      : photoBase64;

    return {
      _multipart: true,
      parts: [
        {
          name: 'FaceDataRecord',
          contentType: 'application/json',
          data: JSON.stringify({ faceLibType: 'blackFD', FDID: '1', FPID: String(employeeNo) }),
        },
        {
          name: 'FaceImage',
          filename: 'face.jpg',
          contentType: 'image/jpeg',
          data: rawBase64,
        },
      ],
    };
  }

  /**
   * Format load people payload for Hikvision
   * Endpoint: /ISAPI/AccessControl/UserInfo/Search?format=json
   *
   * When payload contains person_id or employeeNo, filters by EmployeeNoList for targeted existence checks.
   */
  formatLoadPeoplePayload(payload?: any): any {
    const employeeNo = payload?.employeeNo ?? (payload?.person_id ? md5(payload.person_id) : null);
    if (employeeNo) {
      return {
        UserInfoSearchCond: {
          searchID: '1',
          maxResults: 1,
          searchResultPosition: 0,
          EmployeeNoList: [{ employeeNo }],
        },
      };
    }
    const position = payload?.searchResultPosition ?? 0;
    return {
      UserInfoSearchCond: { searchID: '1', maxResults: 30, searchResultPosition: position },
    };
  }

  /**
   * Parse Hikvision UserInfo/Search response to common format
   * Input: { "UserInfoSearch": { "UserInfo": [{ "employeeNo": "abc", "name": "João", "userType": "normal" }] } }
   * Output: [{ "employeeNo": "abc", "name": "João", "userType": "normal" }]
   */
  parseLoadPeopleResponse(responseData: any): Array<{
    employeeNo: string;
    name: string;
    userType: string;
  }> {
    const userInfoSearch = responseData?.UserInfoSearch || responseData;
    const usersArray = userInfoSearch?.UserInfo || [];

    return usersArray.map((user: any) => ({
      employeeNo: user.employeeNo?.toString() || '',
      name: user.name?.toString() || '',
      userType: user.userType?.toString() || 'normal',
    }));
  }

  /**
   * Get endpoint for operation
   * Handles IP address parsing and port configuration
   */
  getEndpoint(operation: DeviceOperation, device: DeviceInfo): string {
    // Shared with every other device URL — see utils/deviceEndpoint. Hand-rolled copies
    // of this parsing drifted on the port embedded in a DDNS address.
    const base = deviceBaseUrl(device, '/ISAPI/AccessControl');

    switch (operation) {
      case DeviceOperation.SYNC_PERSON:
        return `${base}/UserInfo/Record?format=json`;
      case DeviceOperation.REMOVE_PERSON:
        return `${base}/UserInfo/Delete?format=json`;
      case DeviceOperation.UPLOAD_FACE:
        // Face recognition terminals (DS-K1T341 etc.) reject /ISAPI/AccessControl/UserInfo/SetFace
        // with statusCode=4 "Invalid Operation". They require the FDLib multipart endpoint.
        // The {FPID} placeholder is replaced with the real employeeNo in deviceCommunication.ts.
        return deviceBaseUrl(device, '/ISAPI/Intelligent/FDLib/FaceDataRecord?format=json&faceLibType=blackFD&FDID=1&FPID={FPID}');
      case DeviceOperation.CHECK_HEALTH:
        return deviceBaseUrl(device, '/ISAPI/System/deviceInfo?format=json');
      case DeviceOperation.LOAD_PEOPLE:
        return `${base}/UserInfo/Search?format=json`;
      case DeviceOperation.OPEN_DOOR:
        return `${base}/RemoteControl/door/1`;
      default:
        throw new Error(`Unsupported operation: ${operation}`);
    }
  }

  /**
   * Get HTTP method for operation
   */
  getMethod(operation: DeviceOperation): 'GET' | 'POST' | 'PUT' | 'DELETE' {
    switch (operation) {
      case DeviceOperation.SYNC_PERSON:
        return 'POST';
      case DeviceOperation.REMOVE_PERSON:
        return 'PUT';
      case DeviceOperation.UPLOAD_FACE:
        // FDLib endpoint requires POST (not PUT)
        return 'POST';
      case DeviceOperation.CHECK_HEALTH:
        return 'GET';
      case DeviceOperation.LOAD_PEOPLE:
        return 'POST';
      case DeviceOperation.OPEN_DOOR:
        return 'PUT';
      default:
        return 'POST';
    }
  }

  /**
   * Format payload for Hikvision remote door open
   * Endpoint: PUT /ISAPI/AccessControl/RemoteControl/door/1
   * Content-Type: application/xml
   */
  formatOpenDoorPayload(): any {
    return {
      _xml: true,
      content: '<?xml version="1.0" encoding="UTF-8"?>\n<RemoteControlDoor version="2.0" xmlns="http://www.hikvision.com/ver20/XMLSchema"><cmd>open</cmd></RemoteControlDoor>',
    };
  }

  // ==================== SESSION AUTHENTICATION METHODS ====================

  /**
   * Check if device requires session-based authentication
   * Hikvision firmware < v3.5 requires session auth
   *
   * Backend reference: iot-connect/internal/service/hikvision_operations.go:82-94
   */
  /**
   * Mark this device as digest-only in the firmware cache.
   * Called when session auth is persistently rejected for AccessControl endpoints
   * (the device responds 401 twice even with a fresh session). Digest auth is then
   * used for all subsequent operations without re-running firmware detection.
   */
  markAsDigestOnly(device: DeviceInfo): void {
    const cacheKey = device.ip_address || device.local_ip_address || device.id;
    this.firmwareCache.set(cacheKey, { requiresSession: false, cachedAt: Date.now() });
    console.log(`[Hikvision] Device ${cacheKey} marked as digest-only — session auth rejected for AccessControl`);
  }

  async requiresSessionAuth(device: DeviceInfo): Promise<boolean> {
    console.log(` [Hikvision.requiresSessionAuth] ===== STARTING SESSION AUTH CHECK =====`);
    console.log(` [Hikvision.requiresSessionAuth] Device ID: ${device.id}`);
    console.log(` [Hikvision.requiresSessionAuth] Device IP: ${device.ip_address || device.local_ip_address}`);

    // Check in-memory cache to avoid an extra HTTP roundtrip on every operation.
    // The singleton HikvisionAdapter instance retains this cache for the app's lifetime (TTL: 10 min).
    const cacheKey = device.ip_address || device.local_ip_address || device.id;
    const cached = this.firmwareCache.get(cacheKey);
    if (cached && (Date.now() - cached.cachedAt) < this.FIRMWARE_CACHE_TTL_MS) {
      console.log(` [Hikvision.requiresSessionAuth] Using cached firmware result: requiresSession=${cached.requiresSession}`);
      console.log(` [Hikvision.requiresSessionAuth] ===== RETURNING (CACHED): ${cached.requiresSession} =====`);
      return cached.requiresSession;
    }

    try {
      console.log(` [Hikvision.requiresSessionAuth] Attempting firmware detection...`);
      const firmwareInfo = await this.detectFirmwareVersion(device);

      const requiresSession = firmwareInfo?.requiresSessionAuth || false;
      console.log(` [Hikvision.requiresSessionAuth] Firmware detected successfully:`);
      console.log(`   - Version: ${firmwareInfo?.version}`);
      console.log(`   - Major.Minor.Patch: ${firmwareInfo?.major}.${firmwareInfo?.minor}.${firmwareInfo?.patch}`);
      console.log(`   - Requires Session Auth: ${requiresSession}`);
      console.log(` [Hikvision.requiresSessionAuth] ===== RETURNING: ${requiresSession} =====`);

      // Cache the result to avoid repeating firmware detection on every subsequent operation
      this.firmwareCache.set(cacheKey, { requiresSession, cachedAt: Date.now() });

      return requiresSession;
    } catch (error: any) {
      const errorMessage = error?.message || String(error);

      console.log(` [Hikvision.requiresSessionAuth] Firmware detection failed with error:`);
      console.log(`   - Error: ${errorMessage}`);

      // Handle 401 errors (authentication-related)
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        // Check if account is locked (too many failed attempts)
        if (errorMessage.includes('<lockStatus>lock</lockStatus>')) {
          console.error(` [Hikvision.requiresSessionAuth] ACCOUNT IS LOCKED!`);
          console.error(`   - This is NEW firmware with locked account, NOT old firmware`);
          console.error(`   - DO NOT try session auth, it will fail`);
          console.log(` [Hikvision.requiresSessionAuth] ===== RETURNING: false (ACCOUNT LOCKED) =====`);

          // Throw error to propagate lock status to user
          throw new Error('Device account is locked due to too many failed login attempts. Please wait for unlock or check device credentials.');
        }

        // 401 without lock status = old firmware that requires session auth
        console.log(` [Hikvision.requiresSessionAuth] 401 without lock = OLD FIRMWARE`);
        console.log(` [Hikvision.requiresSessionAuth] ===== RETURNING: true (SESSION AUTH REQUIRED) =====`);
        this.firmwareCache.set(cacheKey, { requiresSession: true, cachedAt: Date.now() });
        return true;
      }

      // Handle connection/TLS errors — skip session auth and fall back to digest.
      // Covers: TCP timeouts, connection refused, TLS protocol mismatch (EPROTO),
      // connection reset (ECONNRESET), and generic request timeout.
      // With minVersion:'TLSv1' set in main.ts, EPROTO should be rare but is still handled here.
      if (
        errorMessage.includes('ETIMEDOUT') || errorMessage.includes('timeout') ||
        errorMessage.includes('ECONNREFUSED') || errorMessage.includes('EPROTO') ||
        errorMessage.includes('ECONNRESET') || errorMessage.includes('SSL') ||
        errorMessage.includes('TLS') || errorMessage.includes('socket hang up')
      ) {
        console.log(` [Hikvision.requiresSessionAuth] Connection/TLS error during firmware detection`);
        console.log(`   - Defaulting to NO session auth (will use digest auth only)`);
        console.log(` [Hikvision.requiresSessionAuth] ===== RETURNING: false (CONN ERROR, NO SESSION) =====`);
        // Don't cache — device may become reachable
        return false; // Skip session auth, use digest only
      }

      // For other unknown errors, re-throw to prevent silent failures
      console.warn(` [Hikvision.requiresSessionAuth] Unknown error (not timeout or 401):`, error);
      throw error;
    }
  }

  /**
   * Detect firmware version to determine authentication method
   * Backend reference: iot-connect/internal/service/hikvision_operations.go:141-164
   *
   * @param device - Device information
   * @returns FirmwareInfo or null if detection fails
   */
  async detectFirmwareVersion(device: DeviceInfo): Promise<FirmwareInfo | null> {
    try {
      console.log(` [Hikvision] Detecting firmware version for device ${device.id}`);

      const endpoint = this.getEndpoint(DeviceOperation.CHECK_HEALTH, device);

      // Make request with digest auth (firmware detection doesn't need session)
      // Use 2-second timeout to fail fast on unreachable devices
      // CRITICAL: Wrap in Promise.race to enforce JavaScript-level timeout
      const timeoutMs = 2000;
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`connect ETIMEDOUT ${device.ip_address || device.local_ip_address}`));
        }, timeoutMs);
      });

      const result = await Promise.race([
        window.electronAPI!.deviceHttpRequest({
          url: endpoint,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          digestAuth: {
            username: device.username,
            password: device.password,
          },
          timeout: timeoutMs,  // 2-second timeout for fast fallback
        }),
        timeoutPromise
      ]);

      if (!result.success) {
        console.warn(` [Hikvision] Firmware detection failed for device ${device.id}: ${result.error}`);
        // CRITICAL: Throw error so requiresSessionAuth() can catch it and check for 401
        throw new Error(result.error || 'Firmware detection failed');
      }

      // Parse response (XML or JSON)
      const deviceInfo = result.data;
      let firmwareVersion = '';

      if (typeof deviceInfo === 'string') {
        // Parse XML response
        const match = deviceInfo.match(/<firmwareVersion>([^<]+)<\/firmwareVersion>/i);
        firmwareVersion = match ? match[1] : '';
      } else if (deviceInfo?.DeviceInfo?.firmwareVersion) {
        // JSON response
        firmwareVersion = deviceInfo.DeviceInfo.firmwareVersion;
      }

      if (!firmwareVersion) {
        console.warn(` [Hikvision] No firmware version found for device ${device.id}`);
        return null;
      }

      // Parse version: "V3.2.30 build 240110" → major=3, minor=2, patch=30
      const versionPart = firmwareVersion.trim().split(/\s+/)[0].replace(/^V/i, '');
      const versionNumbers = versionPart.split('.').map((n) => parseInt(n, 10) || 0);
      const major = versionNumbers[0] || 0;
      const minor = versionNumbers[1] || 0;
      const patch = versionNumbers[2] || 0;

      // Backend logic: requiresSessionAuth = major < 3 || (major == 3 && minor < 5)
      const requiresSessionAuth = major < 3 || (major === 3 && minor < 5);

      const info: FirmwareInfo = {
        version: firmwareVersion,
        major,
        minor,
        patch,
        requiresSessionAuth,
      };

      console.log(
        ` [Hikvision] Detected firmware ${firmwareVersion} for device ${device.id}: requiresSessionAuth=${requiresSessionAuth}`
      );

      return info;
    } catch (error) {
      console.error(` [Hikvision] Error detecting firmware for device ${device.id}:`, error);
      // CRITICAL: Re-throw the error so requiresSessionAuth() can catch it and check for 401
      throw error;
    }
  }

  /**
   * Establish session with device using capabilities endpoint
   * Backend reference: iot-connect/adapters/hikvision/hikvision_adapter.go:834-899
   *
   * @param request - Session establishment request
   * @returns SessionEstablishmentResult with session info
   */
  async establishSession(
    request: SessionEstablishmentRequest
  ): Promise<SessionEstablishmentResult> {
    const { device, firmwareInfo } = request;

    console.log(` [Hikvision.establishSession] ===== STARTING SESSION ESTABLISHMENT =====`);
    console.log(` [Hikvision.establishSession] Device ID: ${device.id}`);
    console.log(` [Hikvision.establishSession] Device IP: ${device.ip_address || device.local_ip_address}`);
    console.log(` [Hikvision.establishSession] Firmware info:`, firmwareInfo);

    // Check if session auth is actually needed
    if (firmwareInfo && !firmwareInfo.requiresSessionAuth) {
      console.log(` [Hikvision.establishSession] Device ${device.id} firmware ${firmwareInfo.version} does NOT require session auth`);
      console.log(` [Hikvision.establishSession] ===== RETURNING: fallbackToDigest =====`);
      return { success: false, fallbackToDigest: true };
    }

    console.log(` [Hikvision.establishSession] Session auth IS required, proceeding...`);

    try {
      // Step 1: Get session capabilities
      const ip = device.ip_address || device.local_ip_address;
      if (!ip) {
        throw new Error('No IP address available for device');
      }

      const cleanIp = ip.replace(/^https?:\/\//, '');
      let deviceIp = cleanIp;
      let port = device.port || '80';
      if (cleanIp.includes(':')) {
        const parts = cleanIp.split(':');
        deviceIp = parts[0];
        port = parts[1];
      }

      const protocol = port === '443' ? 'https' : 'http';
      const baseURL = `${protocol}://${deviceIp}:${port}`;

      // Backend reference: hikvision_adapter.go:857
      const capabilitiesUrl = `${baseURL}/ISAPI/Security/sessionLogin/capabilities?username=${encodeURIComponent(device.username)}`;

      console.log(` [Hikvision.establishSession] Step 1: Requesting session capabilities`);
      console.log(` [Hikvision.establishSession] Capabilities URL: ${capabilitiesUrl}`);
      console.log(` [Hikvision.establishSession] Using username: ${device.username}`);

      const capResult = await window.electronAPI!.deviceHttpRequest({
        url: capabilitiesUrl,
        method: 'GET',
        headers: {
          'Content-Type': 'application/xml',
        },
        digestAuth: {
          username: device.username,
          password: device.password,
        },
        timeout: request.timeout || 10000,
      });

      console.log(` [Hikvision.establishSession] Capabilities request completed:`, {
        success: capResult.success,
        error: capResult.error,
        statusCode: (capResult as any).statusCode,
        hasData: !!capResult.data,
      });

      if (!capResult.success) {
        console.error(` [Hikvision.establishSession] Failed to get session capabilities for device ${device.id}`);
        console.error(` [Hikvision.establishSession] Error: ${capResult.error}`);
        console.error(` [Hikvision.establishSession] Status code: ${(capResult as any).statusCode}`);
        console.log(` [Hikvision.establishSession] ===== RETURNING: fallbackToDigest =====`);
        return {
          success: false,
          error: `Failed to get session capabilities: ${capResult.error}`,
          fallbackToDigest: true,
        };
      }

      // Step 2: Parse XML to extract sessionID
      console.log(` [Hikvision.establishSession] Step 2: Parsing session ID from response`);
      const responseBody = typeof capResult.data === 'string' ? capResult.data : JSON.stringify(capResult.data);
      console.log(` [Hikvision.establishSession] Response body (first 500 chars):`, responseBody.substring(0, 500));

      const sessionIdMatch = responseBody.match(/<sessionID>([^<]+)<\/sessionID>/i);
      const sessionId = sessionIdMatch ? sessionIdMatch[1] : null;

      console.log(` [Hikvision.establishSession] Session ID regex match:`, sessionIdMatch ? 'FOUND' : 'NOT FOUND');

      if (!sessionId) {
        console.error(` [Hikvision.establishSession] No sessionID found in capabilities response for device ${device.id}`);
        console.error(` [Hikvision.establishSession] Full response body:`, responseBody);
        console.log(` [Hikvision.establishSession] ===== RETURNING: fallbackToDigest =====`);
        return {
          success: false,
          error: 'No sessionID found in capabilities response',
          fallbackToDigest: true,
        };
      }

      console.log(` [Hikvision.establishSession] Successfully extracted session ID!`);
      console.log(` [Hikvision.establishSession] Session ID: ${sessionId.substring(0, 10)}...${sessionId.substring(sessionId.length - 10)}`);
      console.log(` [Hikvision.establishSession] Full session ID length: ${sessionId.length}`);

      // Step 3: Generate SessionTag (random SHA256 hash)
      // Backend reference: hikvision_adapter.go:881-886
      console.log(` [Hikvision.establishSession] Step 3: Generating SessionTag (SHA256 hash)`);
      const sessionTag = this.generateSessionTag();
      console.log(` [Hikvision.establishSession] SessionTag generated: ${sessionTag.substring(0, 20)}...`);

      // Step 4: Create session info
      console.log(` [Hikvision.establishSession] Step 4: Creating SessionInfo object`);
      const expiresAt = new Date(Date.now() + 30 * 60 * 1000); // 30 minutes
      const session: SessionInfo = {
        token: sessionId,
        expiresAt: expiresAt,
        headers: {
          SessionTag: sessionTag,
        },
        queryParams: {
          security: '1',
          iv: sessionId,
        },
        vendor: 'hikvision',
        metadata: {
          iv: sessionId,
          sessionTag: sessionTag,
        },
      };

      console.log(` [Hikvision.establishSession] SESSION ESTABLISHED SUCCESSFULLY!`);
      console.log(` [Hikvision.establishSession] Session details:`, {
        token: sessionId.substring(0, 10) + '...',
        expiresAt: expiresAt.toISOString(),
        headers: session.headers,
        queryParams: session.queryParams,
        vendor: session.vendor,
      });
      console.log(` [Hikvision.establishSession] ===== RETURNING: success=true with session =====`);

      return { success: true, session };
    } catch (error: any) {
      console.error(` [Hikvision.establishSession] EXCEPTION during session establishment!`);
      console.error(` [Hikvision.establishSession] Device: ${device.id}`);
      console.error(` [Hikvision.establishSession] Error:`, error);
      console.error(` [Hikvision.establishSession] Error message:`, error.message);
      console.log(` [Hikvision.establishSession] ===== RETURNING: fallbackToDigest =====`);
      return {
        success: false,
        error: error.message || 'Unknown error',
        fallbackToDigest: true,
      };
    }
  }

  /**
   * Generate random SHA256 hash for SessionTag header
   * Backend reference: hikvision_adapter.go:881-886
   */
  private generateSessionTag(): string {
    const randomBytes = Math.random().toString(36).substring(2) + Date.now().toString(36);
    return createHash('sha256').update(randomBytes).digest('hex');
  }

  /**
   * Format URL with session parameters
   * Adds ?security=1&iv=<sessionID> to the endpoint
   *
   * Backend reference: iot-connect/adapters/hikvision/hikvision_adapter.go:979-1067
   */
  formatUrlWithSession(endpoint: string, session: SessionInfo): string {
    if (!session.queryParams) return endpoint;

    const url = new URL(endpoint);
    Object.entries(session.queryParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    return url.toString();
  }

  /**
   * Format headers with session information
   * Adds SessionTag header
   */
  formatHeadersWithSession(
    baseHeaders: Record<string, string>,
    session: SessionInfo
  ): Record<string, string> {
    return {
      ...baseHeaders,
      ...session.headers,
    };
  }

  /**
   * Check if session is still valid (not expired)
   */
  isSessionValid(session: SessionInfo): boolean {
    return new Date() < session.expiresAt;
  }
}
